import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Search, 
  Edit, 
  Eye, 
  Star,
  MapPin,
  Calendar,
  MessageSquare
} from 'lucide-react'

interface Testimonial {
  _id: string
  userId?: string
  name: string
  country?: string
  program?: string
  text: string
  image?: string
  rating?: number
  isActive: boolean
  isFeatured: boolean
  createdAt: string
  updatedAt: string
}

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchTestimonials()
  }, [])

  const fetchTestimonials = async () => {
    try {
      // Mock data for now - replace with actual API call
      const mockTestimonials: Testimonial[] = [
        {
          _id: '1',
          name: 'Sarah Johnson',
          country: 'United States',
          program: '200-Hour Hatha Yoga Teacher Training',
          text: 'The yoga teacher training at Bliss Yoga Rishikesh was absolutely transformative. The instructors were knowledgeable, patient, and truly cared about our growth. I came as a beginner and left as a confident yoga teacher.',
          rating: 5,
          isActive: true,
          isFeatured: true,
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        },
        {
          _id: '2',
          name: 'Michael Chen',
          country: 'Canada',
          program: '300-Hour Advanced Yoga Teacher Training',
          text: 'Amazing experience! The advanced training deepened my understanding of yoga philosophy and teaching methodology. Highly recommended for serious practitioners.',
          rating: 5,
          isActive: true,
          isFeatured: false,
          createdAt: '2024-01-12T14:20:00Z',
          updatedAt: '2024-01-12T14:20:00Z'
        },
        {
          _id: '3',
          name: 'Emma Wilson',
          country: 'Australia',
          program: '200-Hour Ashtanga Yoga Teacher Training',
          text: 'The Ashtanga program was challenging but incredibly rewarding. The teachers were excellent and the community was supportive.',
          rating: 4,
          isActive: true,
          isFeatured: false,
          createdAt: '2024-01-10T09:15:00Z',
          updatedAt: '2024-01-10T09:15:00Z'
        },
        {
          _id: '4',
          name: 'David Brown',
          country: 'United Kingdom',
          program: '200-Hour Hatha Yoga Teacher Training',
          text: 'Great program with experienced teachers. The location in Rishikesh was perfect for a yoga retreat.',
          rating: 4,
          isActive: false,
          isFeatured: false,
          createdAt: '2024-01-08T16:45:00Z',
          updatedAt: '2024-01-08T16:45:00Z'
        }
      ]
      setTestimonials(mockTestimonials)
    } catch (error) {
      console.error('Error fetching testimonials:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredTestimonials = testimonials.filter(testimonial => {
    const matchesSearch = 
      testimonial.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testimonial.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testimonial.program?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testimonial.text.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'ALL' || 
      (statusFilter === 'ACTIVE' && testimonial.isActive) ||
      (statusFilter === 'INACTIVE' && !testimonial.isActive) ||
      (statusFilter === 'FEATURED' && testimonial.isFeatured)

    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (testimonial: Testimonial) => {
    if (!testimonial.isActive) {
      return <span className="badge badge-danger">Inactive</span>
    }
    return <span className="badge badge-success">Active</span>
  }

  const getFeaturedBadge = (testimonial: Testimonial) => {
    return testimonial.isFeatured ? 
      <span className="badge badge-warning">Featured</span> : 
      <span className="badge badge-gray">Regular</span>
  }

  const handleTestimonialAction = (_action: string, testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial)
    setShowModal(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Testimonials</h1>
          <p className="text-gray-600">Manage customer testimonials and reviews</p>
        </div>
        <div className="text-sm text-gray-500">
          {filteredTestimonials.length} of {testimonials.length} testimonials
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Testimonials', value: testimonials.length, color: 'bg-blue-500' },
          { label: 'Active', value: testimonials.filter(t => t.isActive).length, color: 'bg-green-500' },
          { label: 'Featured', value: testimonials.filter(t => t.isFeatured).length, color: 'bg-yellow-500' },
          { label: 'Average Rating', value: (testimonials.reduce((acc, t) => acc + (t.rating || 0), 0) / testimonials.length).toFixed(1), color: 'bg-purple-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search testimonials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
              />
            </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="FEATURED">Featured</option>
          </select>
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTestimonials.map((testimonial, index) => (
          <motion.div
            key={testimonial._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {testimonial.name}
                </h3>
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <MapPin className="w-4 h-4 mr-1" />
                  {testimonial.country}
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(testimonial)}
                  {getFeaturedBadge(testimonial)}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleTestimonialAction('view', testimonial)}
                  className="p-2 text-gray-600 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50"
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleTestimonialAction('edit', testimonial)}
                  className="p-2 text-gray-600 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50"
                  title="Edit Testimonial"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>

            {testimonial.program && (
              <p className="text-sm text-gray-600 mb-3 font-medium">
                {testimonial.program}
                </p>
              )}

              <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                "{testimonial.text}"
              </p>

              {testimonial.rating && (
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < testimonial.rating!
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {testimonial.rating}/5
                  </span>
                </div>
              )}

              <div className="flex items-center text-xs text-gray-500">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(testimonial.createdAt).toLocaleDateString()}
              </div>
          </motion.div>
        ))}
      </div>

      {/* Testimonial Detail Modal */}
      {showModal && selectedTestimonial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-semibold text-gray-900">Testimonial Details</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-ivory-700">Name</label>
                  <p className="text-gray-900">{selectedTestimonial.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-ivory-700">Country</label>
                  <p className="text-gray-900">{selectedTestimonial.country}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-ivory-700">Program</label>
                  <p className="text-gray-900">{selectedTestimonial.program}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-ivory-700">Rating</label>
                  <div className="flex items-center space-x-1">
                    {selectedTestimonial.rating && [...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < selectedTestimonial.rating!
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-gray-600 ml-2">
                      {selectedTestimonial.rating}/5
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-ivory-700">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedTestimonial)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-ivory-700">Featured</label>
                  <div className="mt-1">{getFeaturedBadge(selectedTestimonial)}</div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-ivory-700">Testimonial</label>
                <p className="text-gray-900 mt-1 bg-gray-50 p-4 rounded-lg">
                  "{selectedTestimonial.text}"
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary btn-md"
              >
                Close
              </button>
              <button
                onClick={() => {
                  // Implement edit functionality
                  setShowModal(false)
                }}
                className="btn btn-primary btn-md"
              >
                Edit Testimonial
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Testimonials


